import { useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useAccount } from '../../../contexts/Account';
import ACSnackbar from '../../ACSnackbar';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import { mutationSendInviteContacts } from '../../../services/company_services';
import { AccountType } from '../../../services/utils/types';
import { sendSeeMyProfileEmail } from '../../../services/advisor_services';

const useStyles = () => ({
  textHeadline: {
    fontWeight: '600',
    fontSize: '20px',
    padding: '8px 10px',
    color: '#4D586F',
  },
  textContent: {
    fontWeight: '400',
    fontSize: '14px',
    padding: '8px 10px',
    color: '#001030',
    padding: '0px',
    mb: '16px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function EmailModalContent(props) {
  const { setOpen, isLoading, setIsLoading, forceSnackbar } = props;
  const { accountDetails, type } = useAccount();
  const classes = useStyles();
  const [emails, setEmails] = useState(['', '', '']);
  const [emailErrors, setEmailErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [isInviteButtonDisabled, setIsInviteButtonDisabled] = useState(true);

  const boardOwnerContact = accountDetails.contacts?.find(
    (contact) => contact.isBoardOwner
  );
  const boardOwner = boardOwnerContact
    ? `${boardOwnerContact.givenName} ${boardOwnerContact.surName}`
    : 'No board owner found';

  const handleAddMoreEmails = () => {
    setEmails([...emails, '']);
  };

  function handleClose() {
    setOpen(false);
    setEmails(['', '', '']);
    setEmailErrors([]);
    setIsInviteButtonDisabled(true);
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const updateEmails = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    if (emailErrors[index]) {
      const newErrors = [...emailErrors];
      newErrors[index] = !validateEmail(value) && value !== '';
      setEmailErrors(newErrors);
    }

    setIsInviteButtonDisabled(
      !newEmails.some(validateEmail) || emailErrors.includes(true)
    );
  };

  const handleBlur = () => {
    const newErrors = emails.map(
      (email) => !validateEmail(email) && email !== ''
    );
    setEmailErrors(newErrors);
    setIsInviteButtonDisabled(
      !emails.some(validateEmail) || newErrors.includes(true)
    );
  };

  const handleInvite = async () => {
    setIsLoading(true);
    setIsInviteButtonDisabled(true);
    const validEmails = emails.filter((email) => validateEmail(email));
    const baseUrl = `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`;
    const links = validEmails.map(
      (email) => `${baseUrl}?email=${encodeURIComponent(email)}`
    );

    try {
      await mutationSendInviteContacts({
        EMAILS: validEmails,
        BOARD_OWNER: boardOwner,
        COMPANY_ID: accountDetails.id,
        COMPANY_NAME: accountDetails.companyName,
        LINKS: links,
      });
      setSuccessMessage(
        'Invitations sent successfully! Your contacts will receive an email with a link to join your advisory board.'
      );
      setSuccessSnackbar(true);
      setEmails(['', '', '']);
      setEmailErrors([]);
    } catch (error) {
      console.log('error:', error);
    } finally {
      setIsLoading(false);
      setIsInviteButtonDisabled(false);
    }
  };

  const handleShareLink = async () => {
    setIsLoading(true);
    setIsInviteButtonDisabled(true);
    const validEmails = emails.filter((email) => validateEmail(email));

    try {
      await sendSeeMyProfileEmail({
        EMAILS: validEmails,
        ADVISOR_ID: accountDetails.id,
      });
      setSuccessMessage(
        'Profile shared successfully! Your contacts will receive an email with a link to view your profile.'
      );
      setSuccessSnackbar(true);
      setEmails(['', '', '']);
      setEmailErrors([]);
    } catch (error) {
      console.log('error:', error);
    } finally {
      setIsLoading(false);
      setIsInviteButtonDisabled(false);
    }
  };

  return (
    <>
      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        sx={{ ...classes.textHeadline, padding: '12px 12px' }}
      >
        <img
          src="/icons/email_icon.svg"
          alt={`Email Logo`}
          style={{ marginRight: '12px' }}
        />
        Share via Email
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        {AccountType.COMPANY === type ? (
          <Typography sx={classes.textContent}>
            We've made it easy to invite business contacts and colleagues
            directly to your advisory board. Simply add email addresses below,
            and they will receive an invitation to join your board.
          </Typography>
        ) : (
          <Typography sx={classes.textContent}>
            We've made it easy to invite specific business contacts or
            colleagues to your AdvisoryCloud Profile. Simply add email addresses
            below, and they will receive a direct link to your profile.
          </Typography>
        )}
        <Typography
          sx={{
            fontWeight: '500',
            fontSize: '13px',
            padding: '8px 10px',
            color: '#646D7A',
            padding: '0px',
            mb: '8px',
          }}
        >
          Send Invites
        </Typography>
        <Box sx={{ height: '210px', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'hidden',
              paddingRight: '10px',
              height: '100%',
              width: '100%',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F8F8F8',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '5px',
                backgroundColor: '#C7C7C7',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#B7B7B7',
              },
              '&::-webkit-scrollbar-thumb:active': {
                backgroundColor: '#B7B7B7',
                boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
              },
              '&:hover': {
                overflowY: 'auto',
              },
            }}
          >
            {emails.map((email, index) => (
              <TextField
                variant="outlined"
                key={index}
                placeholder="Email address"
                fullWidth
                value={email}
                onChange={(evt) => updateEmails(index, evt.target.value)}
                onBlur={handleBlur}
                error={emailErrors[index]}
                helperText={emailErrors[index] ? 'Invalid email address' : ''}
                inputProps={{
                  maxLength: 200,
                }}
                InputProps={{
                  style: {
                    borderRadius: '8px',
                  },
                }}
                sx={{
                  marginBottom: '16px',
                }}
              />
            ))}
          </Box>
        </Box>
        <Button
          sx={{
            color: '#646D7A',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            textTransform: 'none',
            marginTop: '0px',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            display: { xxs: 'none', sm: 'flex' },
          }}
          onClick={handleAddMoreEmails}
          id="inviteYourContactsAddMore"
        >
          <Add
            sx={{ color: '#646D7A', fontSize: '18px', marginRight: '5px' }}
          />
          <Typography
            sx={{
              color: '#646D7A',
              fontSize: '13px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '-0.43px',
            }}
          >
            Add more
          </Typography>
        </Button>
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant="contained"
          onClick={
            AccountType.COMPANY === type ? handleInvite : handleShareLink
          }
          disabled={isInviteButtonDisabled}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Send Invitations'}
        </Button>
      </DialogActions>
      <ACSnackbar
        open={successSnackbar}
        text={successMessage}
        severity="success"
        onClose={() => {
          setSuccessSnackbar(false);
          handleClose();
        }}
        autoHideDuration={6000}
        sx={
          forceSnackbar
            ? {
                transform: 'translateX(-0%) !important',
              }
            : {}
        }
      />
    </>
  );
}
